import React, { useState, useEffect } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Stat Card ──────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, iconBg, iconColor, borderAccent, T }) {
  return (
    <div style={{
      background: T.cardBg,
      borderRadius: 14,
      border: `1px solid ${T.cardBorder}`,
      borderTop: `3px solid ${borderAccent}`,
      padding: '18px 20px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 14,
      }}>
        {React.cloneElement(icon, { stroke: iconColor })}
      </div>
      <p style={{ fontSize: 26, fontWeight: 800, color: T.text, margin: '0 0 4px', lineHeight: 1 }}>{value}</p>
      <p style={{ fontSize: 12.5, fontWeight: 600, color: T.text2, margin: '0 0 2px' }}>{label}</p>
      <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{sub}</p>
    </div>
  );
}

// ── Pill badge ─────────────────────────────────────────────────────────
function Pill({ children, bg, color, dot }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11.5, fontWeight: 600, padding: '5px 12px',
      borderRadius: 20, background: bg, color: color,
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />}
      {children}
    </span>
  );
}

// ── List row ───────────────────────────────────────────────────────────
function ListRow({ initial, gradient, title, subtitle, badge, badgeBg, badgeColor, T }) {
  return (
    <div style={{
      padding: '11px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      borderBottom: `1px solid ${T.rowBorder}`, transition: 'background 0.1s', cursor: 'pointer',
    }}
      onMouseEnter={e => e.currentTarget.style.background = T.rowHover}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9, background: gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
        }}>{initial}</div>
        <div>
          <p style={{ fontSize: 12.5, fontWeight: 600, color: T.text, margin: '0 0 1px' }}>{title}</p>
          <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{subtitle}</p>
        </div>
      </div>
      {badge && (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: badgeBg, color: badgeColor, whiteSpace: 'nowrap' }}>
          {badge}
        </span>
      )}
    </div>
  );
}

// ── Section container ─────────────────────────────────────────────────
function Section({ title, sub, action, children, T }) {
  return (
    <div style={{ background: T.cardBg, borderRadius: 14, border: `1px solid ${T.cardBorder}`, overflow: 'hidden' }}>
      <div style={{ padding: '15px 20px', borderBottom: `1px solid ${T.rowBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: 0 }}>{title}</p>
          {sub && <p style={{ fontSize: 11, color: T.text3, margin: '2px 0 0' }}>{sub}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ── Quick action row ───────────────────────────────────────────────────
function ActionRow({ label, icon, color, T }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 11,
        padding: '10px 14px', borderRadius: 10,
        border: `1px solid ${hov ? color + '40' : T.cardBorder}`,
        background: hov ? color + '0a' : 'transparent',
        cursor: 'pointer', textAlign: 'left', transition: 'all 0.12s',
      }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: T.text2, flex: 1 }}>{label}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
    </button>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────
export default function MCADashboard({ onNavigate }) {
  const [llp, setLlp] = useState(null);
  const [stats, setStats] = useState({
    total_companies: 0,
    incorporation_active: 0,
    incorporation_completed: 0,
    resolutions_total: 0,
    dsc_expiring_soon: 0,
    din_kyc_pending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [recentIncorporations, setRecentIncorporations] = useState([]);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('cx-theme') === 'dark');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Stay in sync with theme toggle in MCAWorkspace header
  useEffect(() => {
    const syncTheme = () => setDarkMode(localStorage.getItem('cx-theme') === 'dark');
    window.addEventListener('storage', syncTheme);
    const interval = setInterval(syncTheme, 400);
    return () => { window.removeEventListener('storage', syncTheme); clearInterval(interval); };
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [companiesRes, incorporationsRes, resolutionsRes, llpRes] = await Promise.all([
          fetch(`${BASE}/api/mca/companies`, { headers }),
          fetch(`${BASE}/api/mca/incorporations`, { headers }),
          fetch(`${BASE}/api/mca/resolutions`, { headers }),
          fetch(`${BASE}/api/mca/llp/dashboard`, { headers }),
        ]);
        const companies = await companiesRes.json().catch(() => []);
        const incorporations = await incorporationsRes.json().catch(() => []);
        const resolutions = await resolutionsRes.json().catch(() => []);
        setLlp(await llpRes.json().catch(() => null));

        const companyList = Array.isArray(companies) ? companies : [];
        const incList = Array.isArray(incorporations) ? incorporations : [];
        const resList = Array.isArray(resolutions) ? resolutions : [];

        setRecentCompanies(companyList.slice(0, 6));
        setRecentIncorporations(incList.slice(0, 5));

        setStats({
          total_companies: companyList.length,
          incorporation_active: incList.filter(i => i.status === 'Active').length,
          incorporation_completed: incList.filter(i => i.current_stage === 'COI Received').length,
          resolutions_total: resList.filter(r => r.is_active).length,
          dsc_expiring_soon: 0,
          din_kyc_pending: 0,
        });
      } catch (e) {
        console.error('MCA Dashboard fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // ── Theme tokens — same structure as MCAWorkspace shell ────────────────
  const T = darkMode ? {
    pageBg: '#0f172a',
    cardBg: '#1e293b',
    cardBorder: '#334155',
    rowBorder: '#334155',
    rowHover: '#243044',
    text: '#f1f5f9',
    text2: '#cbd5e1',
    text3: '#94a3b8',
  } : {
    pageBg: '#f8fafc',
    cardBg: '#fff',
    cardBorder: '#eef1f6',
    rowBorder: '#f8fafc',
    rowHover: '#fafbfc',
    text: '#0f1f45',
    text2: '#374151',
    text3: '#9ca3af',
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = (user.full_name || user.name || 'there').split(' ')[0];
  const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  const STAT_CARDS = [
    { label: 'Registered Companies', sub: 'Company Master', value: stats.total_companies, borderAccent: '#00206E',
      iconBg: darkMode ? '#1d2b52' : '#e8edf7', iconColor: darkMode ? '#6d8fe0' : '#00206E',
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg> },
    { label: 'Active Incorporations', sub: 'In progress', value: stats.incorporation_active, borderAccent: '#FC5C0B',
      iconBg: darkMode ? '#3a2412' : '#fff0e6', iconColor: '#FC5C0B',
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
    { label: 'COI Received', sub: 'Completed', value: stats.incorporation_completed, borderAccent: '#059669',
      iconBg: darkMode ? '#0f2e24' : '#e7f7f0', iconColor: '#10b981',
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> },
    { label: 'Active Resolutions', sub: 'Resolution Master', value: stats.resolutions_total, borderAccent: '#7c3aed',
      iconBg: darkMode ? '#2a1f47' : '#f1ebfd', iconColor: '#a78bfa',
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg> },
    { label: 'DSC Expiring', sub: 'Within 30 days', value: stats.dsc_expiring_soon, borderAccent: '#dc2626',
      iconBg: darkMode ? '#3a1414' : '#fde8e8', iconColor: '#f87171',
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> },
    { label: 'DIN KYC Pending', sub: 'Director compliance', value: stats.din_kyc_pending, borderAccent: '#d97706',
      iconBg: darkMode ? '#3a2c0f' : '#fef3cd', iconColor: '#fbbf24',
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
  ];

  const WORKFLOW_STEPS = [
    { label: 'Client Request', desc: 'Service request received', done: true },
    { label: 'Auto Checklist', desc: 'Documents auto-requested', done: true },
    { label: 'Resolution Draft', desc: 'Board resolution generated', done: true },
    { label: 'Event Filing', desc: 'Form workspace preparation', done: false },
    { label: 'MCA Filing', desc: 'Submitted to MCA portal', done: false },
    { label: 'Register Update', desc: 'Statutory registers updated', done: false },
  ];

  const incStageColor = (stage) => {
    const map = darkMode ? {
      'COI Received': { bg: '#0f2e24', color: '#34d399' },
      'Active': { bg: '#1d2b52', color: '#60a5fa' },
      'Name Approved': { bg: '#3a2c0f', color: '#fbbf24' },
    } : {
      'COI Received': { bg: '#dcfce7', color: '#15803d' },
      'Active': { bg: '#dbeafe', color: '#1d4ed8' },
      'Name Approved': { bg: '#fef9c3', color: '#a16207' },
    };
    return map[stage] || (darkMode ? { bg: '#334155', color: '#cbd5e1' } : { bg: '#f1f5f9', color: '#475569' });
  };

  return (
    <div style={{ background: T.pageBg, minHeight: '100vh', transition: 'background 0.2s' }}>

      {/* ── TOP GREETING ROW ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: T.text, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            {greeting}, {firstName} <span>👋</span>
          </h1>
          <p style={{ fontSize: 12.5, color: T.text3, margin: '3px 0 0' }}>
            Your MCA compliance workspace — {today}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Pill bg={darkMode ? '#0f2e24' : '#e7f7f0'} color={darkMode ? '#34d399' : '#15803d'} dot>{loading ? '—' : stats.total_companies} companies</Pill>
          <Pill bg={darkMode ? '#2a1f47' : '#ede9fe'} color={darkMode ? '#c4b5fd' : '#6d28d9'}>MCA WORKSPACE</Pill>
          <button style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
            borderRadius: 20, border: 'none', background: darkMode ? '#f97316' : '#0f1f45', color: '#fff',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Filing
          </button>
        </div>
      </div>

      {/* ── STAT CARDS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 14, marginBottom: 18 }}>
        {STAT_CARDS.map(card => <StatCard key={card.label} {...card} value={loading ? '—' : card.value} T={T} />)}
      </div>

      {/* ── ROW 2 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, marginBottom: 16 }}>

        <Section T={T} title="Recent Companies" sub="Latest additions to Company Master"
          action={<span style={{ fontSize: 11.5, fontWeight: 600, color: darkMode ? '#a78bfa' : '#7c3aed', cursor: 'pointer' }}>View all →</span>}>
          {loading ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading...</div>
          ) : recentCompanies.length === 0 ? (
            <div style={{ padding: '44px 20px', textAlign: 'center' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: darkMode ? '#243044' : '#f4f6f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
              </div>
              <p style={{ fontSize: 12.5, fontWeight: 600, color: T.text2, margin: '0 0 3px' }}>No companies yet</p>
              <p style={{ fontSize: 11.5, color: T.text3, margin: 0 }}>Add via Company Master</p>
            </div>
          ) : (
            recentCompanies.map(c => (
              <ListRow key={c.id} T={T}
                initial={(c.company_name || '?').charAt(0).toUpperCase()}
                gradient="linear-gradient(135deg, #00206E, #1a3a8a)"
                title={c.company_name}
                subtitle={`${c.company_type || 'Private Limited'} · ${c.cin_llpin || 'CIN pending'}`}
                badge="Active" badgeBg={darkMode ? '#1d2b52' : '#e0f2fe'} badgeColor={darkMode ? '#60a5fa' : '#0369a1'}
              />
            ))
          )}
        </Section>

        <Section T={T} title="Filing Automation Flow" sub="MCA filing pipeline">
          <div style={{ padding: '10px 14px' }}>
            {WORKFLOW_STEPS.map((step, idx) => (
              <div key={step.label}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 8px' }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    background: step.done ? (darkMode ? '#f97316' : '#0f1f45') : (darkMode ? '#334155' : '#f1f5f9'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {step.done
                      ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      : <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.text3 }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: step.done ? T.text : T.text3, margin: 0 }}>{step.label}</p>
                    <p style={{ fontSize: 10.5, color: T.text3, margin: 0 }}>{step.desc}</p>
                  </div>
                </div>
                {idx < WORKFLOW_STEPS.length - 1 && <div style={{ marginLeft: 21, width: 2, height: 6, background: T.cardBorder }} />}
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* ── ROW 3 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>

        <Section T={T} title="Recent Incorporations" sub="Active incorporation cases"
          action={<span style={{ fontSize: 11.5, fontWeight: 600, color: darkMode ? '#a78bfa' : '#7c3aed', cursor: 'pointer' }}>View all →</span>}>
          {loading ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading...</div>
          ) : recentIncorporations.length === 0 ? (
            <div style={{ padding: '44px 20px', textAlign: 'center' }}>
              <p style={{ fontSize: 12.5, fontWeight: 600, color: T.text2, margin: '0 0 3px' }}>No incorporation cases</p>
              <p style={{ fontSize: 11.5, color: T.text3, margin: 0 }}>Add via Incorporation module</p>
            </div>
          ) : (
            recentIncorporations.map(inc => {
              const sc = incStageColor(inc.current_stage);
              return (
                <ListRow key={inc.id} T={T}
                  initial={(inc.proposed_name || '?').charAt(0).toUpperCase()}
                  gradient="linear-gradient(135deg, #FC5C0B, #e04800)"
                  title={inc.proposed_name}
                  subtitle={inc.company_type || 'Private Limited'}
                  badge={inc.current_stage || inc.status || 'Active'}
                  badgeBg={sc.bg} badgeColor={sc.color}
                />
              );
            })
          )}
        </Section>

        <Section T={T} title="Quick Actions" sub="Common MCA tasks">
          <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            <ActionRow T={T} label="Add New Company" color="#00206E"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#00206E" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>} />
            <ActionRow T={T} label="New Incorporation" color="#FC5C0B"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#FC5C0B" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>} />
            <ActionRow T={T} label="Event Filing" color="#7c3aed"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} />
            <ActionRow T={T} label="Statutory Registers" color="#059669"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>} />
          </div>
        </Section>
      </div>

      {/* ── LLP COMPLIANCE (LLP Act, 2008) ── */}
      {llp && Number(llp?.counts?.llps) > 0 && (
        <div style={{ marginTop: 16 }}>
          <Section T={T} title="LLP Compliance" sub="Limited Liability Partnership Act, 2008 — Form 11, Form 8, DPIN KYC & Form 3"
            action={<span onClick={() => onNavigate && onNavigate('llp-efiling')} style={{ fontSize: 11.5, fontWeight: 600, color: darkMode ? '#a78bfa' : '#7c3aed', cursor: 'pointer' }}>Open LLP e-Filing →</span>}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0 }}>
              {[
                ['LLPs', llp.counts.llps, '#00206E'],
                ['DPIN KYC pending', llp.counts.dp_kyc_pending, '#d97706'],
                ['Form 11 open', llp.counts.form11_due, '#1d4ed8'],
                ['Form 8 open', llp.counts.form8_due, '#1d4ed8'],
                ['Audit required', llp.counts.audit_required, '#7c3aed'],
                ['Form 3 pending', llp.counts.form3_pending, '#dc2626'],
              ].map(([label, val, color], i) => (
                <div key={label} style={{ padding: '16px 18px', borderLeft: i === 0 ? 'none' : `1px solid ${T.rowBorder}` }}>
                  <p style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: '0 0 3px', lineHeight: 1 }}>{loading ? '—' : (val ?? 0)}</p>
                  <p style={{ fontSize: 11, fontWeight: 600, color: T.text3, margin: 0 }}>{label}</p>
                  <div style={{ height: 2, width: 22, background: color, borderRadius: 2, marginTop: 8 }} />
                </div>
              ))}
            </div>
            {(llp.deadlines || []).length > 0 && (
              <div style={{ borderTop: `1px solid ${T.rowBorder}` }}>
                {(llp.deadlines || []).slice(0, 5).map((d, i) => {
                  const overdue = d.due && llp?.window?.from && d.due < llp.window.from;
                  return (
                    <div key={i} onClick={() => onNavigate && onNavigate(d.kind === 'Form 3' || d.kind === 'DIR-3 KYC' ? 'llp-register' : 'llp-efiling')}
                      style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${T.rowBorder}`, cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = T.rowHover}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div>
                        <p style={{ fontSize: 12.5, fontWeight: 600, color: T.text, margin: '0 0 1px' }}>{d.company_name}</p>
                        <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{d.item}</p>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap',
                        background: overdue ? (darkMode ? '#3a1414' : '#fde8e8') : (darkMode ? '#1d2b52' : '#e0f2fe'),
                        color: overdue ? (darkMode ? '#f87171' : '#dc2626') : (darkMode ? '#60a5fa' : '#0369a1') }}>
                        {d.due ? new Date(d.due).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : d.kind}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>
      )}

    </div>
  );
}